import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";

import { type ClipWord } from "@fitness-app/ai/studio-editor";
import { type StudioClipEdit, type StudioClipPart } from "@fitness-app/data-models/entities/StudioClip";
import { getErrorMessage } from "@fitness-app/utils";

import { backgroundTasksApi } from "~/store/initializeStore";
import { STUDIO_CLIPS_QUERY_KEY } from "./useStudioData";

/** Mirror of the client-web `studio-clip-editor` response. */
export interface StudioClipEditorData {
  clip: {
    id: string;
    hook: string;
    status: string;
    revision: number;
    startMs: number;
    endMs: number;
    edit: StudioClipEdit | null;
    parts: StudioClipPart[] | null;
  };
  /** Ordered source ranges the preview plays: the variant's parts, or the single window. */
  ranges: { startMs: number; endMs: number }[];
  sourceUrl: string;
  words: ClipWord[];
}

export interface RerenderClipInput {
  clipId: string;
  startMs: number;
  endMs: number;
  captionEdits: { wordStartMs: number; text: string }[];
}

export const studioClipEditorQueryKey = (clipId: string) => ["studio", "clip-editor", clipId];

/**
 * Transkrypt wokół klipu i URL źródła — z prywatnego bucketu, więc przez API, nie RLS.
 * Wycinek słów jest liczony wokół AKTUALNEGO okna klipu, dlatego klucz zawiera rewizję i
 * `updatedAt`: gdy wiersz się zmienił (re-render, decyzja, poprawka z zewnątrz), stare słowa
 * mogłyby nie obejmować nowego okna i snap zdań by się wywrócił.
 */
export const useStudioClipEditorData = (clip: { id: string; revision: number; updatedAt: string | null } | null) =>
  useQuery([...studioClipEditorQueryKey(clip?.id ?? ""), clip?.revision ?? 0, clip?.updatedAt ?? ""], {
    enabled: Boolean(clip),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const response = await backgroundTasksApi.get<StudioClipEditorData>("/api/trainers/ai/studio-clip-editor", {
        params: { clipId: clip?.id },
      });
      return response.data;
    },
  });

export const useRerenderClip = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: RerenderClipInput) => {
      const response = await backgroundTasksApi.post<{ clipId: string; revision: number }>(
        "/api/trainers/ai/rerender-studio-clip",
        input,
      );
      return response.data;
    },
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries(STUDIO_CLIPS_QUERY_KEY);
      void queryClient.invalidateQueries(studioClipEditorQueryKey(input.clipId));
      void message.success("Klip renderuje się ponownie. Stara wersja zostaje, aż nowa będzie gotowa.");
    },
    onError: (error) => {
      void message.error(`Nie udało się uruchomić renderu: ${getErrorMessage(error)}`);
    },
  });
};
